import React, {useEffect, useState} from 'react';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import { useForm } from '../hooks/useForm';
import {
    TextField,
    Button,
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Switch,
    FormControlLabel,
    CardActions,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper, MenuItem, IconButton
} from '@mui/material';
import {Male as MaleIcon, Female as FemaleIcon} from '@mui/icons-material';
import {IconSwitch} from "../components/IconSwitch";
import {httpRequest} from "../services/httpService";
import Loader from '../components/Loader';
import { EmptyGuid } from '../utils/constants';
import DateField from '../components/DateField';
import CloseIcon from '@mui/icons-material/Close';

export default function PetForm() {
    const {id} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    // Get clientId from query string
    const query = new URLSearchParams(location.search);
    const clientId = query.get('clientId');

    const validate = (data) => {
        const errors = {};
        if (!data.name) errors.name = 'Nombre es requerido';
        return errors;
    };

    const [visits, setVisits] = useState([]);
    const [loadingVisits, setLoadingVisits] = useState(false);

    const {
        data,
        isLoading,
        error,
        validationErrors,
        handleInputChange,
        handleSubmit: baseHandleSubmit,
        isNew,
        close,
    } = useForm({
        id,
        fetchUrl: id ? `/api/pet/${id}` : null,
        saveUrl: '/api/pet',
        validate,
    });

    // Custom handleSubmit to inject clientId if new
    const handleSubmit = (e) => {
        if (isNew && clientId && data) {
            data.clientId = clientId;
        }
        baseHandleSubmit(e);
    };

    const handleCreateVisit = (pet) => {
        // Navigate to VisitForm with EmptyGuid and petId as query param
        navigate(`/visits/${EmptyGuid}?petId=${pet.id}`);
    };

    useEffect(() => {
        const loadVisits = async () => {
            if (!id) return; // Ensure id is checked inside the function
            setLoadingVisits(true);
            try {
                const response = await httpRequest(`/api/visit/pet/${id}`, { method: 'GET' });
                setVisits(response);
            } catch (error) {
                console.error('Error loading visits:', error);
                setVisits([]); // Set visits to an empty array on error
            } finally {
                setLoadingVisits(false);
            }
        };

        loadVisits(); // Always call the function, but it will handle the id check internally
    }, [id]);

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mx: 'auto' }}>
            {/* Header */}
            <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
            >
                <Typography variant="h5">
                    {isNew ? 'Nueva Mascota' : 'Editar Mascota'}
                </Typography>
                <IconButton variant="text" onClick={close}>
                    <CloseIcon fontSize="large"/>
                </IconButton>
            </Box>
            <Card sx={{ padding: 2 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                variant="standard"
                                fullWidth
                                label="Nombre"
                                name="name"
                                value={data.name || ''}
                                onChange={handleInputChange}
                                error={!!validationErrors.name}
                                helperText={validationErrors.name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                variant="standard"
                                fullWidth
                                label="Especie"
                                name="type"
                                value={data.type ?? ''}
                                onChange={(e) => handleInputChange({ target: { name: 'type', value: e.target.value } })}
                                error={!!validationErrors.type}
                                helperText={validationErrors.type}
                            >
                                <MenuItem value={0}>Perro</MenuItem>
                                <MenuItem value={1}>Gato</MenuItem>
                                <MenuItem value={2}>Pájaro</MenuItem>
                                <MenuItem value={3}>Ratón</MenuItem>
                                <MenuItem value={4}>Pez</MenuItem>
                                <MenuItem value={5}>Otro</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                variant="standard"
                                fullWidth
                                label="Color"
                                name="color"
                                value={data.color || ''}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                variant="standard"
                                fullWidth
                                label="Raza"
                                name="breed"
                                value={data.breed || ''}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DateField
                                label="Fecha de Nacimiento"
                                name="birthDay"
                                value={data.birthDay || ''}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <FormControlLabel sx={{mt: 2}}
                                              control={
                                                  <IconSwitch
                                                      checked={!!data.isMale}
                                                      onChange={(e) =>
                                                          handleInputChange({
                                                              target: {name: 'isMale', value: e.target.checked},
                                                          })
                                                      }
                                                      iconOn={<MaleIcon />}
                                                      iconOff={<FemaleIcon />}
                                                  />
                                              }
                                              label={data.isMale ? 'Macho' : 'Hembra'}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <FormControlLabel sx={{mt:2}}
                                control={
                                    <Switch
                                        checked={!!data.isSterilized}
                                        onChange={(e) =>
                                            handleInputChange({
                                                target: { name: 'isSterilized', value: e.target.checked },
                                            })
                                        }
                                    />
                                }
                                label="Esterilizado"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                variant="standard"
                                fullWidth
                                label="Número de Historia"
                                name="historyNumber"
                                value={data.historyNumber || ''}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                variant="standard"
                                fullWidth
                                label="Microchip"
                                name="chipNumber"
                                value={data.chipNumber || ''}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="standard"
                                fullWidth
                                multiline
                                rows={2}
                                label="Notas"
                                name="comment"
                                value={data.comment || ''}
                                onChange={handleInputChange}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions>
                    <Button type="submit" variant="contained" color="primary">
                        Guardar
                    </Button>
                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => handleCreateVisit(data)}
                        disabled={isNew}
                    >
                        Crear visita
                    </Button>
                </CardActions>
            </Card>

            {/* Visits Section */}
            <Typography variant="h6" sx={{ mt: 4 }}>
                Visitas
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small" hover="true">
                    <TableHead>
                        <TableRow>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Motivo</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingVisits ? (
                            <TableRow>
                                <TableCell colSpan={2} align="center">Loading visits...</TableCell>
                            </TableRow>
                        ) : visits.map((visit) => (
                            <TableRow
                                key={visit.id}
                                onClick={() => navigate(`/visits/${visit.id}`)}
                                hover
                                style={{ cursor: 'pointer' }}
                            >
                                <TableCell>{visit.time}</TableCell>
                                <TableCell>{visit.reason}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

